import { authToken } from '../storage/authToken';
import * as Breadcrumbs from './breadcrumbs';

const META_URL = '/meta.json';
const DEDUPE_WINDOW_MS = 15000;
const dedupeCache = new Map();

let metaRequest = null;
let frontendIncidentContext = {};

function trimString(value, maxLength = 4000) {
	if (typeof value !== 'string') {
		return value ?? null;
	}

	return value.length > maxLength ? `${value.slice(0, maxLength - 3)}...` : value;
}

function safeJson(value) {
	if (value == null) {
		return null;
	}

	try {
		return JSON.parse(JSON.stringify(value));
	} catch (error) {
		return {
			serialization_error: error?.message || 'Unable to serialize payload',
		};
	}
}

export function setFrontendIncidentContext(context = {}) {
	frontendIncidentContext = {
		...context,
		metadata: safeJson(context.metadata || null),
	};
}

function joinUrl(baseUrl, path) {
	if (!baseUrl) {
		return path;
	}

	return `${baseUrl.replace(/\/+$/, '')}/${path.replace(/^\/+/, '')}`;
}

function getMonitoringUrl() {
	if (process.env.REACT_APP_FRONTEND_MONITORING_URL) {
		return process.env.REACT_APP_FRONTEND_MONITORING_URL;
	}

	return joinUrl(process.env.REACT_APP_BASE_URL || '/api/', 'frontend-incidents');
}

async function loadMeta() {
	if (!metaRequest) {
		metaRequest = fetch(META_URL, {
			cache: 'no-store',
			headers: {
				Accept: 'application/json',
			},
		})
			.then(async (response) => {
				if (!response.ok) {
					throw new Error(`meta request failed with status ${response.status}`);
				}

				return response.json();
			})
			.catch(() => null);
	}

	return metaRequest;
}

function buildFingerprint(payload) {
	return [
		payload.type || 'unknown',
		payload.route_path || window.location.pathname,
		payload.message || 'no-message',
		payload.build_version || 'no-build',
	].join('::');
}

function shouldSkipFingerprint(fingerprint) {
	const now = Date.now();
	const previous = dedupeCache.get(fingerprint);

	if (previous && now - previous < DEDUPE_WINDOW_MS) {
		return true;
	}

	dedupeCache.set(fingerprint, now);
	return false;
}

function normalizePayload(payload, meta) {
	const routePath = `${window.location.pathname}${window.location.search}${window.location.hash}`;

	return {
		source: 'pharmacy-portal',
		type: trimString(payload.type || 'frontend_error', 120),
		severity: trimString(payload.severity || 'error', 40),
		message: trimString(payload.message || 'Unknown frontend error'),
		stack: trimString(payload.stack || null, 20000),
		component_stack: trimString(payload.component_stack || null, 20000),
		page_url: trimString(window.location.href, 2000),
		route_path: trimString(payload.route_path || routePath, 1000),
		build_version: trimString(payload.build_version || meta?.version || null, 255),
		build_commit: trimString(payload.build_commit || meta?.commit || null, 64),
		environment: trimString(process.env.NODE_ENV || 'development', 32),
		user_agent: trimString(window.navigator?.userAgent || null, 2000),
		pharmacy_id: payload.pharmacy_id ?? null,
		pharmacy_user_id: payload.pharmacy_user_id ?? null,
		pharmacy_name: trimString(payload.pharmacy_name || null, 255),
		module_name: trimString(payload.module_name || null, 255),
		metadata: safeJson(payload.metadata || null),
		// How the user got here — labels only, see ./breadcrumbs.
		breadcrumbs: Breadcrumbs.snapshot(),
		occurred_at: payload.occurred_at || new Date().toISOString(),
	};
}

export async function reportFrontendIncident(payload = {}) {
	if (typeof window === 'undefined') {
		return;
	}

	try {
		const meta = await loadMeta();
		const payloadWithContext = {
			...frontendIncidentContext,
			...payload,
			metadata: {
				...(frontendIncidentContext.metadata || {}),
				...(payload.metadata || {}),
			},
		};
		const normalized = normalizePayload(payloadWithContext, meta);
		const fingerprint = buildFingerprint(normalized);

		if (shouldSkipFingerprint(fingerprint)) {
			return;
		}

		const token = authToken();

		await fetch(getMonitoringUrl(), {
			method: 'POST',
			keepalive: true,
			headers: {
				Accept: 'application/json',
				'Content-Type': 'application/json',
				...(token ? { Authorization: `Bearer ${token}` } : {}),
			},
			body: JSON.stringify({
				...normalized,
				fingerprint,
			}),
		});
	} catch (_) {
		// Reporting must never break the application.
	}
}

export default reportFrontendIncident;
